/* =====================================================================
   v2 — Header davranışı
   - yapışkanlık modu (always / up / none) — bölüm ayarından gelir
   - kaydırınca yapışkan başlık gölgelenir (.is-stuck)
   - tam ekran mobil menü
   - dokunmatik masaüstünde mega menü (hover yoksa tıklama ile)
   - arama paneli ve ülke/para birimi seçici
   ===================================================================== */
class V2Header extends HTMLElement {
  connectedCallback() {
    // Mobil menü bölüm kökünün dışında (bkz. v2-header.liquid yorumu),
    // bu yüzden bulunamazsa Shopify'ın bölüm sarmalayıcısına bakıyoruz.
    this.menu =
      this.querySelector('[data-v2-mobile-menu]') ||
      (this.parentNode && this.parentNode.querySelector
        ? this.parentNode.querySelector('[data-v2-mobile-menu]')
        : null);
    this.burger = this.querySelector('[data-v2-burger]');
    this.megaWraps = Array.prototype.slice.call(this.querySelectorAll('[data-v2-mega]'));

    this.initSticky();

    this.onScroll = this.onScroll.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('keydown', this.onKeydown);
    this.onScroll();

    if (this.burger && this.menu) {
      this.burger.addEventListener('click', () => this.toggleMenu(!this.isMenuOpen));
      this.menu.querySelectorAll('[data-v2-menu-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.toggleMenu(false));
      });
      /* Menüden bir bağlantıya gidilince menü kapansın */
      this.menu.querySelectorAll('a[href]').forEach((link) => {
        link.addEventListener('click', () => this.toggleMenu(false));
      });
    }

    this.initSearch();
    this.initLocalization();

    /* Birden fazla mega/açılır menü olabilir: her biri kendi tetikleyicisine
       bağlanır, biri açılınca diğerleri kapanır. */
    this.megaWraps.forEach((wrap) => {
      const toggle = wrap.querySelector('[data-v2-mega-toggle]');
      if (!toggle) return;
      wrap.addEventListener('pointerenter', () => this.fitMega(wrap));
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const open = !wrap.classList.contains('is-open');
        if (open) this.fitMega(wrap);
        this.closeMegas();
        wrap.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
      });
    });

    if (this.megaWraps.length) {
      document.addEventListener('click', (event) => {
        const inside = this.megaWraps.some((wrap) => wrap.contains(event.target));
        if (!inside) this.closeMegas();
      });
    }
  }

  /* Panel nav öğesine göre ortalanır; geniş panel (ör. 3 sütunlu) kenara
     yakın bir öğede ekrandan taşmasın diye yatayda kaydırılır. Ok ucu
     aynı miktarda ters yöne gider, yine tetikleyiciyi gösterir. */
  fitMega(wrap) {
    const panel = wrap.querySelector('.v2-mega');
    if (!panel) return;
    const gutter = 20;
    const rect = wrap.getBoundingClientRect();
    const width = panel.offsetWidth;
    const left = rect.left + rect.width / 2 - width / 2;
    const maxLeft = document.documentElement.clientWidth - gutter - width;
    const shift = Math.min(Math.max(left, gutter), Math.max(maxLeft, gutter)) - left;
    panel.style.setProperty('--v2-mega-shift', Math.round(shift) + 'px');
  }

  closeMegas() {
    this.megaWraps.forEach((wrap) => {
      wrap.classList.remove('is-open');
      const toggle = wrap.querySelector('[data-v2-mega-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('keydown', this.onKeydown);
  }

  get isMenuOpen() {
    return !!this.menu && this.menu.classList.contains('is-open');
  }

  /* ---------- Yapışkanlık ---------- */
  initSticky() {
    // Yapışkanlığı taşıyan öğe <v2-header> değil, Shopify'ın bölüm
    // sarmalayıcısı (bkz. v2-header.css'teki .v2-header-section notu).
    this.section = this.closest('.v2-header-section') || this.parentElement;
    this.stickyMode = this.dataset.v2Sticky || 'always';
    this.lastScroll = window.scrollY;

    if (!this.section) return;

    if (this.stickyMode === 'none') {
      // Liquid zaten kapsam stiliyle aynı kuralı basıyor; sınıf, tema
      // editöründe ayar değişince yeniden yükleme beklemesin diye.
      this.section.classList.add('v2-header-section--static');
      return;
    }

    this.section.classList.remove('v2-header-section--static');
    this.classList.toggle('v2-header--autohide', this.stickyMode === 'up');
  }

  /* Bir panel açıkken başlığı kaydırıp gizlemek paneli de götürürdü. */
  get hasOpenPanel() {
    if (this.isMenuOpen) return true;
    if (this.search && !this.search.hasAttribute('hidden')) return true;
    if (this.loc && this.loc.classList.contains('is-open')) return true;
    return this.megaWraps.some((wrap) => wrap.classList.contains('is-open'));
  }

  setHidden(hidden) {
    if (!this.section) return;
    this.section.classList.toggle('is-hidden', hidden);
  }

  onScroll() {
    const y = window.scrollY;
    this.classList.toggle('is-stuck', y > 8);

    if (this.stickyMode !== 'up') {
      this.lastScroll = y;
      return;
    }

    // Küçük titremeler yön değiştirmiş sayılmasın; başlığın kendi
    // yüksekliğini geçmeden de gizlenmesin, yoksa sayfa başında zıplıyor.
    const delta = y - this.lastScroll;
    if (Math.abs(delta) < 6) return;

    if (y <= this.offsetHeight || this.hasOpenPanel) {
      this.setHidden(false);
    } else {
      this.setHidden(delta > 0);
    }

    this.lastScroll = y;
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    if (this.isMenuOpen) this.toggleMenu(false);
    this.closeMegas();
    this.toggleSearch(false);
    this.toggleLoc(false);
  }

  /* ---------- Arama paneli ---------- */
  initSearch() {
    this.search = this.querySelector('[data-v2-search]');
    this.searchToggle = this.querySelector('[data-v2-search-toggle]');
    if (!this.search || !this.searchToggle) return;

    this.searchToggle.addEventListener('click', () => {
      this.toggleSearch(this.search.hasAttribute('hidden'));
    });

    this.querySelectorAll('[data-v2-search-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.toggleSearch(false));
    });
  }

  toggleSearch(open) {
    if (!this.search || !this.searchToggle) return;
    this.search.toggleAttribute('hidden', !open);
    this.searchToggle.setAttribute('aria-expanded', String(open));
    if (open) {
      const input = this.search.querySelector('[data-v2-search-input]');
      if (input) input.focus();
    }
  }

  /* ---------- Ülke / para birimi ---------- */
  initLocalization() {
    this.loc = this.querySelector('[data-v2-loc]');
    if (!this.loc) return;

    this.locToggle = this.loc.querySelector('[data-v2-loc-toggle]');
    if (!this.locToggle) return;

    this.locToggle.addEventListener('click', () => {
      this.toggleLoc(!this.loc.classList.contains('is-open'));
    });

    document.addEventListener('click', (event) => {
      if (this.loc.contains(event.target)) return;
      this.toggleLoc(false);
    });
  }

  toggleLoc(open) {
    if (!this.loc || !this.locToggle) return;
    this.loc.classList.toggle('is-open', open);
    this.locToggle.setAttribute('aria-expanded', String(open));
  }

  toggleMenu(open) {
    if (!this.menu) return;
    this.menu.classList.toggle('is-open', open);
    if (this.burger) {
      this.burger.setAttribute('aria-expanded', String(open));
    }
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      const first = this.menu.querySelector('[data-v2-menu-close]');
      if (first) first.focus();
    } else if (this.burger) {
      this.burger.focus();
    }
  }
}

if (!customElements.get('v2-header')) {
  customElements.define('v2-header', V2Header);
}
